import React from 'react';
import { useLocation } from 'react-router-dom';
import authService from '../services/authService';

export default function GlobalAlerts() {
  const [showLow, setShowLow] = React.useState(false);
  const [thresholdCents, setThresholdCents] = React.useState(50);
  const [balanceCents, setBalanceCents] = React.useState(null);
  const location = useLocation();

  const fetchAndEval = React.useCallback(async () => {
    try {
      const user = await authService.getUserInfo();
      const roles = (user?.roles || []).map(r => (r||'').toLowerCase());
      const isTeam = roles.includes('teamuser') || roles.includes('teamadmin');
      const isEnt = roles.includes('enterpriseuser') || roles.includes('enterpriseadmin');
      const isAdmin = roles.includes('admin');
      // Treat prior Pro with zero balance as Free: only show low-balance when currently paid
      let isPaidEffective = isTeam || isEnt || isAdmin;
      if (!isPaidEffective && roles.includes('paiduser')) {
        // Check balance; if zero → treat as not paid
        const t = authService.getToken && authService.getToken();
        const headersPre = t ? { Authorization: `Bearer ${t}` } : {};
        try {
          const rBalPre = await fetch('/api/billing/balance', { headers: headersPre, credentials: 'include', cache: 'no-store' });
          if (rBalPre.ok) {
            const ctBalPre = rBalPre.headers.get('content-type') || '';
            const jPre = ctBalPre.includes('application/json') ? (await rBalPre.json()) : null;
            const balPre = typeof jPre?.balanceCents === 'number' ? jPre.balanceCents : 0;
            isPaidEffective = balPre > 0;
          }
        } catch { isPaidEffective = false; }
      }
      if (!isPaidEffective) { setShowLow(false); return; }
      const t = authService.getToken && authService.getToken();
      const headers = t ? { Authorization: `Bearer ${t}` } : {};
      const [rThr, rBal] = await Promise.all([
        fetch('/api/billing/low-balance-threshold', { headers, credentials: 'include' }),
        fetch('/api/billing/balance', { headers, credentials: 'include' })
      ]);
      // Parse threshold JSON if available; fallback to 50 only for threshold
      let thrJson = { lowBalanceWarnCents: 50 };
      try {
        const ctThr = rThr.headers.get('content-type') || '';
        if (rThr.ok && ctThr.includes('application/json')) {
          thrJson = await rThr.json();
        }
      } catch {}
      // Parse balance JSON only if available; do NOT default to 0 on failure
      let balCents = null;
      try {
        const ctBal = rBal.headers.get('content-type') || '';
        if (rBal.ok && ctBal.includes('application/json')) {
          const balJson = await rBal.json();
          balCents = typeof balJson?.balanceCents === 'number' ? balJson.balanceCents : null;
        }
      } catch {}
      setThresholdCents(thrJson.lowBalanceWarnCents ?? 50);
      if (balCents != null) setBalanceCents(balCents);
      const dismissed = sessionStorage.getItem('lowBalanceDismissed') === 'true';
      // Only evaluate/show when we have a valid balance number
      const thrCents = thrJson.lowBalanceWarnCents ?? 50;
      if (balCents != null) {
        if (!dismissed && balCents <= thrCents) {
          setShowLow(true);
        } else {
          setShowLow(false);
        }
      } else {
        // No balance available (e.g., not logged in yet or request failed) → do not show
        setShowLow(false);
      }
    } catch {
      setShowLow(false);
    }
  }, []);

  React.useEffect(() => { fetchAndEval(); }, [fetchAndEval, location.pathname]);

  // Listen for dismissal from other components (e.g., NewDashboard)
  React.useEffect(() => {
    const handleDismissal = () => {
      setShowLow(false);
    };
    window.addEventListener('lowBalanceDismissed', handleDismissal);
    return () => {
      window.removeEventListener('lowBalanceDismissed', handleDismissal);
    };
  }, []);

  const handleDismiss = React.useCallback(() => {
    setShowLow(false);
    sessionStorage.setItem('lowBalanceDismissed', 'true');
    // Dispatch custom event to notify other components (e.g., NewDashboard)
    window.dispatchEvent(new CustomEvent('lowBalanceDismissed'));
  }, []);

  if (!showLow) return null;
  return (
    <div className="modal-backdrop" onClick={handleDismiss}>
      <div className="app-modal" onClick={e=>e.stopPropagation()}>
        <div className="app-modal-header">
          <strong>Low Balance Warning</strong>
          <button className="close" onClick={handleDismiss}>×</button>
        </div>
        <div className="app-modal-body">
          <p>Your balance is {balanceCents!=null ? `$${((balanceCents||0)/100).toFixed(2)}` : 'low'} which is at/below your warning level of <strong>${((thresholdCents||0)/100).toFixed(2)}</strong>.</p>
          <div style={{ textAlign:'right', marginTop:12 }}>
            <button className="primary" onClick={handleDismiss}>OK</button>
          </div>
        </div>
      </div>
    </div>
  );
}


